import React, { useMemo, useState } from "react";
import classNames from "classnames";
import styles from "./brands-list-links.module.scss";
import { FixLink } from "../fix-link";
import { useTranslation } from "react-i18next";
import { Url } from "@/helpers/url";
import { Gtag } from "@/services/gtag";
import type { B2cBrand } from "@/generated/graphql/b2c";
import fonts from "@/style/fonts.module.scss";
import iconRight from "@/assets/right-outlined.svg";
import iconDown from "@/assets/down-outlined.svg";
import { useLayoutType, LayoutStyleTypes, LayoutTypes } from "@/util-react/layout";
import { Icon } from "@/elements-react/fix-icon";
import { FixButton } from "@/elements-react/fix-button";

export interface BrandsListLinksProps {
  type: LayoutStyleTypes.TYPE_SM | LayoutStyleTypes.TYPE_LG;
  brands: B2cBrand[];
  className?: string;
}

const COUNTS = { sm: 10, lg: 15 };

export const BrandsListLinks: React.FC<BrandsListLinksProps> = ({ type, brands, className }) => {
  const { t } = useTranslation();

  const [cursor, setCursor] = useState(Math.min(COUNTS[type], brands.length));

  const list = useMemo(
    () =>
      brands.slice(0, cursor).map((brand) => ({
        ...brand,
        url: Url.to("dynamic-route", { dynamicPath: [brand.slug] }),
      })),
    [brands, cursor],
  );

  const isShowMoreVisible = brands.length > cursor;

  const handleShowMore = () => {
    setCursor((prev) => Math.min(prev + COUNTS[type], brands.length));
    Gtag.pushEvent("more_car_brand_click", "");
  };

  return (
    <div className={classNames(styles.root, styles[type], className)}>
      <div className={styles.itemList}>
        {list.map((item) => (
          <FixLink className={styles.item} href={item.url} key={item.id} ariaLabel={item.name} onClick={() => Gtag.pushEvent("car_brand_click", item.name)}>
            <span className={styles.itemInner}>
              <span className={classNames(styles.itemText, fonts["font-base-normal"])}>{item.name}</span>
              <Icon src={iconRight} width={8} />
            </span>
          </FixLink>
        ))}
      </div>

      {isShowMoreVisible && (
        <div className={styles.controls}>
          <FixButton className={classNames(styles.buttonMore)} variant={'more'} onClick={handleShowMore} suffix={<Icon src={iconDown} width={8} />}>
            {t("76vwwhyjifi741oi.more-car-brands", "More car brands")}
          </FixButton>
        </div>
      )}
    </div>
  );
};
